/**
 * `onboarding-invitation` (crossmarc/steward-app#1764, docs/35 R2 "jump ahead"): a firm invited
 * a client to its onboarding form. One row per send-able invitation, in the FIRM's workspace,
 * written by the façade under the firm caller's own bearer token on the user plane, the same
 * shape as `intake-invitation` (`intakeInvitation.ts`). Never written by a background role.
 *
 * ## What the row is, and what it is not
 *
 * It is the firm's record of the invitation: which address, which sections the firm asked for,
 * the personal note that went into the email, who sent it and when, and whether it is still
 * live. The profile page joins it to the `advisor-client` record by `emailKey`
 * ({@link normalizeClientEmailKey}), the same server-written address docs/35 lets invitations
 * join on.
 *
 * It is NOT the client's right to sign up. That is appcore's onboarding signup grant
 * (crossmarc/appcore#735), minted by the same façade route right after this row is written and
 * revoked by cancel. The grant's existence is not copied here: `state` plus the mint's 2xx is
 * the evidence. Outside appcore's admission paths the grant is read by `GET /me`'s
 * `onboardingInvitations` (the CLIENT's view, keyed on the address stored on their user record,
 * which appcore's post-authentication self-heal can write without `email_verified`,
 * crossmarc/appcore#743) and by appcore's account lookup (the firm's own grants only).
 *
 * Nothing reads this row as authority. A client never has a membership in the firm workspace,
 * so they cannot read it at all; the client-side welcome page is driven by `/me`, not by this.
 *
 * ## States
 *
 *   - `invited`   — the email was sent (or the send was attempted after the row and the grant
 *                   were written; see the handler's "state precedes send").
 *   - `cancelled` — the firm cancelled it. The grant is revoked before the row flips and again
 *                   after, so a `cancelled` row has no live grant behind it from this firm,
 *                   unless that second revoke failed (logged only; the handler's "Cancel").
 *                   Kept, never deleted.
 */
import { z } from 'zod';

export const ONBOARDING_INVITATION_TYPE_CODE = 'onboarding-invitation';

export const ONBOARDING_INVITATION_STATES = ['invited', 'cancelled'] as const;
export type OnboardingInvitationState = (typeof ONBOARDING_INVITATION_STATES)[number];

/**
 * The sections a firm can ask for beyond the client's own details, which are always asked.
 * Order is the dialog's order (the "Firm: invite to onboarding" mock board). The form itself is
 * crossmarc/steward-app#1765; these keys are what it reads to decide what to show.
 */
export const ONBOARDING_OPTIONAL_SECTIONS = ['spouse', 'trustedContact', 'beneficiaries', 'aboutYou'] as const;
export type OnboardingOptionalSection = (typeof ONBOARDING_OPTIONAL_SECTIONS)[number];

/** The dialog's defaults: spouse, trusted contact and beneficiary wishes on; About you off
 *  (issue #1764, "What" item 2). */
export const ONBOARDING_SECTION_DEFAULTS: Readonly<Record<OnboardingOptionalSection, boolean>> = {
  spouse: true,
  trustedContact: true,
  beneficiaries: true,
  aboutYou: false,
};

/** The personal note's cap, in characters. It goes into the email verbatim (escaped), so it is
 *  kept short enough to read as a note rather than a letter. */
export const ONBOARDING_NOTE_MAX = 500;

/** The longest client name the firm may type on the invitation (a display label, not a legal name). */
export const ONBOARDING_CLIENT_NAME_MAX = 120;

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();
/** Storage-shape check, matching `intakeInvitation.ts`'s private copy; the façade's input schema
 *  is what rejects a malformed address before a row is written. */
const emailShape = z.string().min(3).max(320);

export const OnboardingInvitationAttributesSchema = z.strictObject({
  firmId: id,
  /** The address the advisor typed, as typed: the address the email goes to. */
  email: emailShape,
  /** {@link normalizeClientEmailKey} of `email`: the join key to the `advisor-client` record and
   *  the one-live-invitation-per-address key. */
  emailKey: id,
  /** The client's name as the advisor typed it, trimmed. Firm-entered DISPLAY data only: it
   *  labels the invitation on the firm's own screens and is NEVER put in the email (house rule
   *  B1; `onboardingInviteEmail.ts` takes no name parameter at all). Absent when they typed none. */
  clientName: z.string().min(1).max(ONBOARDING_CLIENT_NAME_MAX).optional(),
  askSpouse: z.boolean(),
  askTrustedContact: z.boolean(),
  askBeneficiaries: z.boolean(),
  askAboutYou: z.boolean(),
  /** The personal note as the advisor typed it, trimmed. Plain text: the email escapes it and
   *  never renders it as markup. Absent when they wrote none. */
  note: z.string().max(ONBOARDING_NOTE_MAX).optional(),
  state: z.enum(ONBOARDING_INVITATION_STATES),
  createdAt: instant,
  createdBy: id,
  lastInvitedAt: instant,
  /** Whether the address already had a Steward login at the LAST send, as appcore's account
   *  lookup answered it. A point-in-time reading, not a live fact: it decides the email's wording
   *  ("create your login" or "sign in") and what the firm's screens say the client saw. Absent
   *  when the lookup could not be made; the email then says "create your login".
   *
   *  Recorded here rather than asked for before sending because appcore answers the lookup only
   *  for an address the firm has already minted a grant for (security review 2026-09-24): asking
   *  before any grant exists would be an account-existence oracle. */
  hasAccount: z.boolean().optional(),
  /** When the invitation email was last DELIVERED to the mail provider's acceptance (the mail
   *  outcome `sent`), set by the create and resend paths of the invitation core and by no other
   *  writer. A row without it reads as "the invitation has not gone out": the core writes the row
   *  BEFORE the grant mint and the mail, so a failed mint or mail, or a mail that came back
   *  `suppressed` or `skipped`, leaves a live row nobody has been told about. There is no
   *  backfill: a row written before this field existed reads as not delivered, and the next
   *  send of it is a resend that sets it (crossmarc/steward-app#2283, fix round 1). Not proof
   *  the client read it, only that the provider accepted it. */
  deliveredAt: instant.optional(),
  cancelledAt: instant.optional(),
  cancelledBy: id.optional(),
});

export type OnboardingInvitationAttributes = z.infer<typeof OnboardingInvitationAttributesSchema>;

/** Reads a stored row's attributes; `unreadable` covers both "does not parse" and "missing". */
export function readOnboardingInvitation(
  value: unknown,
): { kind: 'invitation'; invitation: OnboardingInvitationAttributes } | { kind: 'unreadable' } {
  const parsed = OnboardingInvitationAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'invitation', invitation: parsed.data } : { kind: 'unreadable' };
}

/** The asked-for sections of a row, keyed the way the SPA and #1765 read them. */
export function onboardingSectionsOf(
  invitation: Pick<OnboardingInvitationAttributes, 'askSpouse' | 'askTrustedContact' | 'askBeneficiaries' | 'askAboutYou'>,
): Record<OnboardingOptionalSection, boolean> {
  return {
    spouse: invitation.askSpouse,
    trustedContact: invitation.askTrustedContact,
    beneficiaries: invitation.askBeneficiaries,
    aboutYou: invitation.askAboutYou,
  };
}

/**
 * The asked-for optional sections as the NAMES appcore's onboarding signup grant carries
 * (`askedSections`, crossmarc/steward-app#1882), in {@link ONBOARDING_OPTIONAL_SECTIONS} order.
 * The grant is what the client's form read can reach, because the client has no access to the
 * invitation row in the firm's workspace. Every mint sends the row's CURRENT choices, so a
 * resend that changes them overwrites the earlier list.
 */
export function askedSectionsOf(
  invitation: Pick<OnboardingInvitationAttributes, 'askSpouse' | 'askTrustedContact' | 'askBeneficiaries' | 'askAboutYou'>,
): OnboardingOptionalSection[] {
  const asked = onboardingSectionsOf(invitation);
  return ONBOARDING_OPTIONAL_SECTIONS.filter((s) => asked[s]);
}
